import { useMemo, useState } from 'react';
import { Field } from '../ui';
import { GIORNI, POSIZIONI, aRrule, daRrule, descrivi, preset, vuota } from '../../utils/recurrence';

const campo = 'rounded border border-line bg-surface px-2 py-2 text-sm';

/**
 * Editor guidato delle ripetizioni: i casi frequenti in un menu, il resto in un
 * pannello. La RRULE grezza resta disponibile ma non è più la via principale.
 */
export default function RecurrenceBuilder({ rrule, inizio, onChange }) {
  const stato = useMemo(() => daRrule(rrule), [rrule]);
  const presets = useMemo(() => preset(inizio), [inizio]);
  const corrisponde = presets.find(p => p.stato && aRrule(p.stato, inizio) === rrule);
  const [personalizzata, setPersonalizzata] = useState(!!rrule && !corrisponde);

  const scelta = personalizzata ? 'custom' : (corrisponde?.id ?? (rrule ? 'custom' : ''));
  const aggiorna = nuovo => onChange(aRrule(nuovo, inizio));

  const scegliPreset = valore => {
    if (valore === 'custom') {
      setPersonalizzata(true);
      if (!rrule) aggiorna({ ...vuota(), freq: 'WEEKLY' });
      return;
    }
    setPersonalizzata(false);
    const trovato = presets.find(p => p.id === valore);
    onChange(trovato?.stato ? aRrule(trovato.stato, inizio) : '');
  };

  const commutaGiorno = id => aggiorna({
    ...stato,
    byday: stato.byday.includes(id) ? stato.byday.filter(g => g !== id) : [...stato.byday, id],
  });

  return (
    <div className="space-y-3">
      <Field label="Ripetizione">
        {props => (
          <select {...props} value={scelta} onChange={e => scegliPreset(e.target.value)}>
            {presets.map(p => <option key={p.id} value={p.id}>{p.etichetta}</option>)}
          </select>
        )}
      </Field>

      {personalizzata && (
        <div className="space-y-3 rounded-lg border border-line p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-ink">
              Ripeti ogni
              <input type="number" min={1} max={999} aria-label="Ogni quante unità"
                value={stato.interval} className={`${campo} ml-2 w-20`}
                onChange={e => aggiorna({ ...stato, interval: Number(e.target.value) })} />
            </label>
            <select aria-label="Unità di ripetizione" className={campo} value={stato.freq}
              onChange={e => aggiorna({ ...stato, freq: e.target.value })}>
              <option value="DAILY">giorni</option>
              <option value="WEEKLY">settimane</option>
              <option value="MONTHLY">mesi</option>
              <option value="YEARLY">anni</option>
            </select>
          </div>

          {stato.freq === 'WEEKLY' && (
            <fieldset>
              <legend className="mb-1 text-sm text-ink">Nei giorni</legend>
              <div className="flex flex-wrap gap-1">
                {GIORNI.map(g => (
                  <button key={g.id} type="button" aria-pressed={stato.byday.includes(g.id)}
                    onClick={() => commutaGiorno(g.id)}
                    className={`min-h-[36px] w-12 rounded-full border text-sm transition-colors ${
                      stato.byday.includes(g.id)
                        ? 'border-brand-500 bg-brand-50 font-medium text-brand-700'
                        : 'border-line text-muted hover:text-ink'}`}>
                    {g.breve}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {stato.freq === 'MONTHLY' && (
            <fieldset className="space-y-2">
              <legend className="text-sm text-ink">Ogni mese</legend>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input type="radio" name="mensile" checked={stato.mensile === 'giorno'}
                  onChange={() => aggiorna({ ...stato, mensile: 'giorno' })} />
                il giorno
                <input type="number" min={1} max={31} aria-label="Giorno del mese"
                  className={`${campo} w-20`} value={stato.bymonthday || ''}
                  onChange={e => aggiorna({ ...stato, mensile: 'giorno', bymonthday: Number(e.target.value) })} />
              </label>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <input type="radio" name="mensile" checked={stato.mensile === 'posizione'}
                  onChange={() => aggiorna({ ...stato, mensile: 'posizione' })} />
                <select aria-label="Posizione nel mese" className={campo} value={stato.bysetpos}
                  onChange={e => aggiorna({ ...stato, mensile: 'posizione', bysetpos: Number(e.target.value) })}>
                  {POSIZIONI.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
                <select aria-label="Giorno della settimana" className={campo} value={stato.bydayPos}
                  onChange={e => aggiorna({ ...stato, mensile: 'posizione', bydayPos: e.target.value })}>
                  {GIORNI.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                </select>
              </label>
            </fieldset>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm text-ink">Fine</legend>
            {[['mai', 'Mai'], ['il', 'Il giorno'], ['dopo', 'Dopo un numero di volte']].map(([valore, etichetta]) => (
              <label key={valore} className="flex flex-wrap items-center gap-2 text-sm">
                <input type="radio" name="fine" checked={stato.fine === valore}
                  onChange={() => aggiorna({ ...stato, fine: valore })} />
                {etichetta}
                {valore === 'il' && stato.fine === 'il' && (
                  <input type="date" aria-label="Data di fine" className={campo} value={stato.until}
                    onChange={e => aggiorna({ ...stato, fine: 'il', until: e.target.value })} />
                )}
                {valore === 'dopo' && stato.fine === 'dopo' && (
                  <input type="number" min={1} max={10000} aria-label="Numero di ripetizioni"
                    className={`${campo} w-24`} value={stato.count}
                    onChange={e => aggiorna({ ...stato, fine: 'dopo', count: Number(e.target.value) })} />
                )}
              </label>
            ))}
          </fieldset>

          <details>
            <summary className="cursor-pointer text-xs text-muted">Regola RRULE (avanzato)</summary>
            <input aria-label="Regola RRULE" className={`${campo} mt-2 w-full font-mono`} value={rrule}
              onChange={e => onChange(e.target.value)} />
          </details>
        </div>
      )}

      <p className="rounded bg-surface-2 px-3 py-2 text-sm text-ink" aria-live="polite">
        {descrivi(daRrule(rrule), inizio)}
      </p>
    </div>
  );
}
